import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

import type { MediaImage, PropertyRoomTypeWithImages } from '../types';
import { DEFAULT_ROOM_NAME, HERO_ROOM_NAME } from '../utils';
import type { UploadedRoomAssignment } from '../utils';

import ImagesByRoomRow from './Row';
import { EMPTY_IMAGE_PUBLIC_IDS, buildRoomImagesByKey, getRoomKey, pruneUnavailableRoomImages } from './utils';
import type { RoomImagesByKey } from './utils';

interface Props {
  excludedImagePublicIds?: Array<string>;
  hiddenImagePublicIds?: Array<string>;
  isMetadataEditable?: boolean;
  mediaImages: Array<MediaImage>;
  onAssignImagesToRoom: (roomId: string, roomName: string, selectedImages: Array<MediaImage>) => void;
  onEditImage: (publicId: string) => void;
  onOpenImagesVideo?: () => void;
  onRemoveImageFromRoom: (publicId: string) => void;
  onToggleHiddenImage: (publicId: string) => void;
  onUpdateImageTitle?: (publicId: string, title: string) => void;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  showHeader?: boolean;
  showRoomDetails?: boolean;
  uploadedRoomAssignments?: Array<UploadedRoomAssignment>;
  visibleRoomKeys?: Array<string>;
}

function buildPropertyRoomTypesKey(propertyRoomTypes: Array<PropertyRoomTypeWithImages>) {
  return propertyRoomTypes
    .map((room, index) => {
      const imagePublicIds = (room.images ?? []).map((image) => image.id_cloudinary_external).join(',');

      return [getRoomKey(room, index), room.name, imagePublicIds].join(':');
    })
    .join('|');
}

export default function ImagesByRoom({
  excludedImagePublicIds = EMPTY_IMAGE_PUBLIC_IDS,
  hiddenImagePublicIds = EMPTY_IMAGE_PUBLIC_IDS,
  isMetadataEditable = false,
  mediaImages,
  onAssignImagesToRoom,
  onEditImage,
  onOpenImagesVideo,
  onRemoveImageFromRoom,
  onToggleHiddenImage,
  onUpdateImageTitle,
  propertyRoomTypes = [],
  showHeader = true,
  showRoomDetails = true,
  uploadedRoomAssignments = [],
  visibleRoomKeys,
}: Props) {
  const [roomImagesByKey, setRoomImagesByKey] = useState<RoomImagesByKey>(() =>
    buildInitialRoomImagesByKey(propertyRoomTypes, excludedImagePublicIds, mediaImages),
  );
  const dismissedUploadedRoomAssignmentPublicIds = useRef<Set<string>>(new Set());
  const propertyRoomTypesKey = useMemo(() => buildPropertyRoomTypesKey(propertyRoomTypes), [propertyRoomTypes]);
  const previousPropertyRoomTypesKey = useRef(propertyRoomTypesKey);

  const dismissUploadedRoomAssignments = useCallback((publicIds: Array<string>) => {
    if (publicIds.length === 0) {
      return;
    }

    dismissedUploadedRoomAssignmentPublicIds.current = new Set([
      ...dismissedUploadedRoomAssignmentPublicIds.current,
      ...publicIds,
    ]);
  }, []);

  useEffect(() => {
    const hasRoomSourceChanged = previousPropertyRoomTypesKey.current !== propertyRoomTypesKey;

    if (hasRoomSourceChanged) {
      previousPropertyRoomTypesKey.current = propertyRoomTypesKey;
      dismissedUploadedRoomAssignmentPublicIds.current = new Set();
      setRoomImagesByKey(buildInitialRoomImagesByKey(propertyRoomTypes, excludedImagePublicIds, mediaImages));
      return;
    }

    setRoomImagesByKey((currentRoomImagesByKey) => pruneUnavailableRoomImages(currentRoomImagesByKey, mediaImages));
  }, [excludedImagePublicIds, mediaImages, propertyRoomTypes, propertyRoomTypesKey]);

  useEffect(() => {
    if (uploadedRoomAssignments.length === 0) {
      return;
    }

    const activeUploadedRoomAssignments = uploadedRoomAssignments.filter(
      (assignment) => !dismissedUploadedRoomAssignmentPublicIds.current.has(assignment.publicId),
    );

    if (activeUploadedRoomAssignments.length === 0) {
      return;
    }

    const mediaImagesByPublicId = new Map(mediaImages.map((image) => [image.publicId, image]));

    setRoomImagesByKey((currentRoomImagesByKey) => {
      let nextRoomImagesByKey = currentRoomImagesByKey;
      const processedPublicIds: Array<string> = [];

      activeUploadedRoomAssignments.forEach((assignment) => {
        const mediaImage = mediaImagesByPublicId.get(assignment.publicId);

        if (!mediaImage) {
          return;
        }

        processedPublicIds.push(assignment.publicId);

        const currentRoomImages = nextRoomImagesByKey[assignment.roomId] ?? [];

        if (currentRoomImages.some((image) => image.publicId === assignment.publicId)) {
          return;
        }

        const selectedPublicIds = new Set([assignment.publicId]);
        const nextRoomImagesWithoutSelection = Object.fromEntries(
          Object.entries(nextRoomImagesByKey).map(([roomKey, roomImages]) => [
            roomKey,
            roomKey === assignment.roomId
              ? roomImages
              : roomImages.filter((image) => !selectedPublicIds.has(image.publicId)),
          ]),
        );

        nextRoomImagesByKey = {
          ...nextRoomImagesWithoutSelection,
          [assignment.roomId]: currentRoomImages.concat({
            publicId: mediaImage.publicId,
            title: mediaImage.title,
          }),
        };
      });

      dismissUploadedRoomAssignments(processedPublicIds);

      return nextRoomImagesByKey;
    });
  }, [dismissUploadedRoomAssignments, mediaImages, uploadedRoomAssignments]);

  useEffect(() => {
    const roomKeyById = new Set(propertyRoomTypes.map((room, index) => getRoomKey(room, index)));

    if (roomKeyById.size === 0) {
      return;
    }

    setRoomImagesByKey((currentRoomImagesByKey) =>
      syncRoomImagesWithMediaRooms(currentRoomImagesByKey, mediaImages, roomKeyById),
    );
  }, [mediaImages, propertyRoomTypes]);

  const handleAssignImagesToRoom = (roomId: string, roomName: string, selectedImages: Array<MediaImage>) => {
    dismissUploadedRoomAssignments(selectedImages.map((image) => image.publicId));
    onAssignImagesToRoom(roomId, roomName, selectedImages);
  };

  const handleRemoveImageFromRoom = (publicId: string) => {
    if (uploadedRoomAssignments.some((assignment) => assignment.publicId === publicId)) {
      dismissUploadedRoomAssignments([publicId]);
    }

    onRemoveImageFromRoom(publicId);
  };

  if (propertyRoomTypes.length === 0) {
    return null;
  }

  const visibleRoomKeySet = visibleRoomKeys ? new Set(visibleRoomKeys) : undefined;
  const visibleRoomTypes = propertyRoomTypes
    .map((room, index) => ({ room, index }))
    .filter(({ room, index }) => !visibleRoomKeySet || visibleRoomKeySet.has(getRoomKey(room, index)));

  const roomRows = (
    <Stack divider={showRoomDetails ? <Divider /> : undefined} sx={{ px: showRoomDetails ? 3 : 0 }}>
      {visibleRoomTypes.map(({ room, index }) => {
        const roomKey = getRoomKey(room, index);

        return (
          <ImagesByRoomRow
            key={roomKey}
            hiddenImagePublicIds={hiddenImagePublicIds}
            isMetadataEditable={isMetadataEditable}
            mediaImages={mediaImages}
            onAssignImagesToRoom={handleAssignImagesToRoom}
            onEditImage={onEditImage}
            onOpenImagesVideo={onOpenImagesVideo}
            onRemoveImageFromRoom={handleRemoveImageFromRoom}
            onToggleHiddenImage={onToggleHiddenImage}
            onUpdateImageTitle={onUpdateImageTitle}
            room={room}
            roomImages={roomImagesByKey[roomKey] ?? []}
            roomKey={roomKey}
            setRoomImagesByKey={setRoomImagesByKey}
            showRoomDetails={showRoomDetails}
          />
        );
      })}
    </Stack>
  );

  if (!showHeader) {
    return roomRows;
  }

  return (
    <Paper variant="outlined" sx={{ mt: 4, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1}
        sx={{ px: 3, py: 2.5 }}
      >
        <Typography variant="h5">Images by room</Typography>
        <Typography variant="body2" color="text.secondary">
          Each row matches a room in{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            Rooms
          </Box>
          . Add as many carousel images as needed.
        </Typography>
      </Stack>
      <Divider />
      {roomRows}
    </Paper>
  );
}

function syncRoomImagesWithMediaRooms(
  roomImagesByKey: RoomImagesByKey,
  mediaImages: Array<MediaImage>,
  roomKeyById: Set<string>,
) {
  let hasChanged = false;
  let nextRoomImagesByKey = roomImagesByKey;

  mediaImages.forEach((mediaImage) => {
    const targetRoomKey =
      mediaImage.sourceRoomId && roomKeyById.has(mediaImage.sourceRoomId) ? mediaImage.sourceRoomId : undefined;
    const isUnassigned = mediaImage.sourceLabel === DEFAULT_ROOM_NAME || mediaImage.sourceLabel === HERO_ROOM_NAME;
    const existingRoomKey = Object.entries(nextRoomImagesByKey).find(([_roomKey, roomImages]) =>
      roomImages.some((roomImage) => roomImage.publicId === mediaImage.publicId),
    )?.[0];

    if (!targetRoomKey && !isUnassigned) {
      return;
    }

    if (isUnassigned) {
      if (!existingRoomKey) {
        return;
      }

      nextRoomImagesByKey = removeRoomImage(nextRoomImagesByKey, mediaImage.publicId);
      hasChanged = true;
      return;
    }

    const targetRoomImages = nextRoomImagesByKey[targetRoomKey] ?? [];
    const isAlreadyInTargetRoom = targetRoomImages.some((roomImage) => roomImage.publicId === mediaImage.publicId);

    const nextRoomImage = {
      publicId: mediaImage.publicId,
      title: mediaImage.title,
    };

    if (isAlreadyInTargetRoom) {
      const nextTargetRoomImages = targetRoomImages.map((roomImage) =>
        roomImage.publicId === mediaImage.publicId ? nextRoomImage : roomImage,
      );

      if (nextTargetRoomImages === targetRoomImages || areRoomImagesEqual(targetRoomImages, nextTargetRoomImages)) {
        return;
      }

      nextRoomImagesByKey = {
        ...nextRoomImagesByKey,
        [targetRoomKey]: nextTargetRoomImages,
      };
      hasChanged = true;
      return;
    }

    nextRoomImagesByKey = {
      ...removeRoomImage(nextRoomImagesByKey, mediaImage.publicId),
      [targetRoomKey]: targetRoomImages.concat(nextRoomImage),
    };
    hasChanged = true;
  });

  return hasChanged ? nextRoomImagesByKey : roomImagesByKey;
}

function removeRoomImage(roomImagesByKey: RoomImagesByKey, publicId: string) {
  return Object.fromEntries(
    Object.entries(roomImagesByKey).map(([roomKey, roomImages]) => [
      roomKey,
      roomImages.filter((roomImage) => roomImage.publicId !== publicId),
    ]),
  );
}

function areRoomImagesEqual(
  firstRoomImages: Array<{ publicId: string; title?: string }>,
  secondRoomImages: Array<{ publicId: string; title?: string }>,
) {
  return (
    firstRoomImages.length === secondRoomImages.length &&
    firstRoomImages.every(
      (firstRoomImage, index) =>
        firstRoomImage.publicId === secondRoomImages[index].publicId &&
        firstRoomImage.title === secondRoomImages[index].title,
    )
  );
}

function buildInitialRoomImagesByKey(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages>,
  excludedImagePublicIds: Array<string>,
  mediaImages: Array<MediaImage>,
) {
  return pruneUnavailableRoomImages(buildRoomImagesByKey(propertyRoomTypes, excludedImagePublicIds), mediaImages);
}
